import React, { useEffect, useRef, useState } from 'react';

interface StreamingTextProps {
  /** The full text content to display */
  text: string;
  /** Whether the message is actively streaming (receiving new chunks). When false, renders text immediately. */
  isStreaming?: boolean;
  /**
   * When true, always start visibleLength at 0 on mount and typewriter-animate
   * to the full text length — even when `isStreaming` is false. Use this as a
   * fallback when SSE chunks arrive in a burst and React never observes
   * `isStreaming=true` during render.
   */
  animateOnMount?: boolean;
  /** Called once when the mount animation finishes revealing all text (animateOnMount only). */
  onRevealComplete?: () => void;
  /** Characters revealed per animation frame. Default 6 — smooth but keeps up with fast streams. */
  charsPerFrame?: number;
  /** Render children with the revealed text as argument */
  children: (visibleText: string) => React.ReactNode;
}

/**
 * StreamingText — typewriter reveal for AI agent responses.
 *
 * When `isStreaming` is true, new characters are revealed ~60fps using
 * requestAnimationFrame so the text "types in" as it streams. This avoids
 * the jarring "batch pop" that happens when large SSE chunks arrive together.
 *
 * When `animateOnMount` is true the same typewriter runs on first mount,
 * independent of `isStreaming`. This covers the case where SSE buffering
 * causes the entire response to land in one React batch.
 *
 * When `isStreaming` becomes false (message complete), the remaining hidden
 * characters are flushed immediately so the user never sees a stuck cursor.
 */
export function StreamingText({
  text,
  isStreaming = false,
  animateOnMount = false,
  onRevealComplete,
  charsPerFrame = 6,
  children,
}: StreamingTextProps) {
  const [isMountAnimating, setIsMountAnimating] = useState(animateOnMount);
  const onRevealCompleteRef = useRef(onRevealComplete);
  onRevealCompleteRef.current = onRevealComplete;

  const textLengthRef = useRef(text.length);
  textLengthRef.current = text.length;

  const [visibleLength, setVisibleLength] = useState(() =>
    (isStreaming || animateOnMount) ? 0 : text.length
  );
  const rafRef = useRef<number | null>(null);

  // Completion effect for animateOnMount — safely run side-effects after render phase
  useEffect(() => {
    if (isMountAnimating && text.length > 0 && visibleLength >= text.length) {
      setIsMountAnimating(false);
      onRevealCompleteRef.current?.();
    }
  }, [isMountAnimating, visibleLength, text.length]);

  // Main animation effect — handles both streaming and mount-reveal
  useEffect(() => {
    // Flush immediately when not streaming AND not in a mount animation
    if (!isStreaming && !isMountAnimating) {
      if (visibleLength !== text.length) {
        setVisibleLength(text.length);
      }
      if (rafRef.current !== null) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
      return;
    }

    const animate = () => {
      setVisibleLength((prev) => {
        const next = Math.min(prev + charsPerFrame, textLengthRef.current);
        if (next < textLengthRef.current) {
          rafRef.current = requestAnimationFrame(animate);
        } else {
          rafRef.current = null;
        }
        return next;
      });
    };

    if (rafRef.current === null && visibleLength < text.length) {
      rafRef.current = requestAnimationFrame(animate);
    }

    return () => {
      if (rafRef.current !== null) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
    };
  }, [text.length, isStreaming, charsPerFrame, isMountAnimating]);

  // When text shrinks (e.g. on a message reset) reset counter.
  useEffect(() => {
    if (text.length < visibleLength) {
      setIsMountAnimating(false);
      setVisibleLength(isStreaming ? 0 : text.length);
    }
  }, [text.length, visibleLength, isStreaming]);

  const visibleText = text.slice(0, visibleLength);
  return <>{children(visibleText)}</>;
}

export default StreamingText;
